/**
 * Typed zone chat when playing online (ported from apps/c0-online/src/ui/zoneChat.ts): Enter opens a line at the bottom
 * left, Enter sends it as a `say` command (the zone server relays it to everyone near as `zoneSay`), Escape closes it.
 * Lines heard go into the HUD's chat box under โลก, and as a bubble over the speaker (net/netWorld.ts turns each into a
 * `bark`). Does nothing in a local game (no `window.__net`). While the line is open the game's keys are off (input.ts
 * ignores keys typed into an input).
 */
import { SAY_MAX } from '@nine/shared';
import { h } from './dom';
import { registerUi } from './registry';

const online = () => !!(window as unknown as { __net?: unknown }).__net;

registerUi('zoneChat', ({ send }) => {
  if (!online()) return {};
  const box = document.createElement('input');
  box.type = 'text';
  box.id = 'zoneSay';
  box.maxLength = SAY_MAX;
  box.placeholder = 'พิมพ์ข้อความ · Enter ส่ง · Esc ปิด';
  box.autocomplete = 'off';
  box.hidden = true;
  Object.assign(box.style, {
    position: 'fixed',
    left: 'calc(var(--g-gap, 12px) + env(safe-area-inset-left, 0px))',
    bottom: 'calc(var(--g-gap, 12px) + 140px)',
    width: 'clamp(260px, calc(50vw - 350px), 340px)',
    zIndex: '60',
    padding: '6px 10px',
    font: '14px/1.3 inherit',
    color: '#fff3d8',
    background: 'rgba(20,12,10,.82)',
    border: '1px solid #c9a24a',
    borderRadius: '6px',
    outline: 'none',
  });
  document.body.append(box);
  const close = () => {
    box.hidden = true;
    box.value = '';
    box.blur();
  };
  // Capture phase: Enter must not also reach the game (talk / interact) when it opens or sends the line.
  addEventListener(
    'keydown',
    (e) => {
      const typing = (e.target as HTMLElement | null)?.closest?.('input, textarea, select');
      if (e.key === 'Enter' && document.activeElement !== box && !typing) {
        e.preventDefault();
        e.stopImmediatePropagation();
        box.hidden = false;
        box.focus();
      } else if (document.activeElement === box) {
        e.stopPropagation();
        if (e.key === 'Escape') close();
        else if (e.key === 'Enter') {
          const text = box.value.trim();
          if (text) send({ t: 'say', text });
          close();
        }
      }
    },
    true,
  );
  // Leaving the line must not leave the character walking.
  box.addEventListener('blur', () => (box.hidden = true));
  return {
    handle(ev) {
      if (ev.type !== 'zoneSay') return;
      const chat = document.getElementById('chat');
      if (!chat) return;
      // The HUD's own line shape for the world channel (ui/hud.ts `chat`).
      chat.append(h('div', { class: 'c-world' }, h('b', {}, 'โลก'), ' ', `${ev.name}: ${ev.text}`));
      while (chat.children.length > 9) chat.firstElementChild?.remove();
      while (chat.children.length > 1 && (chat.firstElementChild as HTMLElement).offsetTop < 0)
        chat.firstElementChild?.remove();
    },
    escape() {
      if (box.hidden) return false;
      close();
      return true;
    },
  };
});
